import { useEffect, useState, type JSX } from 'react';

import { cn } from '../lib/cn.js';
import { AvatarLia } from '../lia/AvatarLia.js';
import {
  IconAI, IconCheck, IconClose, IconMagnifyingGlass, IconModule, IconNewFile, IconPen1, IconViewDocumentFiles,
} from '../icons/index.js';
import { SECONDES_AVANT_LE_COMPTEUR, SECONDES_AVANT_LE_LONG, type EtapeDeLiaVue } from './attentesLia.js';

export interface LiaAuTravailProps {
  /** La derniere etape que Lia annonce ; absente : « Lia réfléchit… ». */
  etape?: EtapeDeLiaVue | null | undefined;
  /** Depuis quand elle travaille : le compteur « Toujours en cours (N s). » en part. */
  depuis: Date;
  /** Le travail a echoue ou le delai est depasse : la bulle le dit (en `text-danger-ink`) au lieu de charger. */
  echec?: string | null | undefined;
  /** Le panneau etroit (24rem du centre) : avatar et texte plus petits. */
  compact?: boolean | undefined;
  /** Le libelle de repli sans etape (defaut « Lia réfléchit… »). */
  statut?: string | undefined;
  /** Le texte au-dela de 60 s (defaut : « C’est plus long que d’habitude (N s). »). */
  detailLong?: ((secondes: number) => string) | undefined;
  /** L'instant courant en ms (rendu statique, test) : absent, la bulle tient son horloge, d'une seconde. */
  maintenant?: number | undefined;
  className?: string | undefined;
}

/** L'icone de l'outil que Lia utilise (celle de la conversation privee avec Lia). */
function IconeDOutil({ kind, size }: { kind: string; size: number }): JSX.Element {
  if (kind === 'search') return <IconMagnifyingGlass size={size} />;
  if (kind === 'create') return <IconNewFile size={size} />;
  if (kind === 'update') return <IconPen1 size={size} />;
  if (kind === 'discover') return <IconModule size={size} />;
  if (kind === 'studio') return <IconAI size={size} />;
  return <IconViewDocumentFiles size={size} />;
}

/**
 * LIA AU TRAVAIL : sa bulle pendant qu'elle cherche, le rendu EXACT de la
 * conversation privee (`packages/shell/src/centre/ConversationLia.tsx`) : son
 * avatar, « Lia », puis l'icone de l'outil, le libelle de la derniere etape et
 * un rond qui tourne (« Terminé », « Échec » ou « Refusé » quand l'etape est
 * finie), ou « Lia réfléchit… » sans etape. Au-dela de 15 s, « Toujours en
 * cours (N s). ». Un echec ou un delai depasse se dit, sans rester bloque.
 * Les deux endroits qui montrent Lia au travail (la conversation privee, le
 * fil d'un canal, d'un direct, d'une fiche) rendent CE composant.
 */
export function LiaAuTravail({ compact, className, ...etat }: LiaAuTravailProps): JSX.Element {
  const enEchec = etat.echec !== undefined && etat.echec !== null && etat.echec !== '';
  const avatar = compact ? 24 : 32;
  const texte = compact ? 'text-[0.8125rem] leading-[1.1875rem]' : 'text-sm leading-[1.3125rem]';
  return <article data-lia-au-travail={enEchec ? 'echec' : 'en-cours'}
    className={cn('grid gap-x-3 py-2.5', compact ? 'grid-cols-[1.5rem_minmax(0,1fr)]' : 'grid-cols-[2rem_minmax(0,1fr)]', className)}>
    <AvatarLia size={avatar} />
    <div className={cn('min-w-0 text-ink-900', texte)}>
      <div className={cn('flex items-baseline gap-2', compact ? 'text-xs' : 'text-[0.8125rem]')}><span className="font-semibold">Lia</span></div>
      <EtatDeLiaAuTravail {...etat} />
    </div>
  </article>;
}

/**
 * LE CORPS de la bulle, sans avatar ni nom : l'etape (ou « Lia réfléchit… »)
 * et le compteur. La conversation privee le pose dans SA bulle, qui porte
 * aussi le texte partiel et l'erreur ; le fil d'un canal passe par `LiaAuTravail`.
 */
export function EtatDeLiaAuTravail({ etape, depuis, echec, statut, detailLong, maintenant }: Omit<LiaAuTravailProps, 'compact' | 'className'>): JSX.Element {
  const [horloge, setHorloge] = useState(() => Date.now());
  const enEchec = echec !== undefined && echec !== null && echec !== '';
  useEffect(() => {
    if (maintenant !== undefined || enEchec) return;
    setHorloge(Date.now());
    const minuterie = window.setInterval(() => setHorloge(Date.now()), 1000);
    return () => window.clearInterval(minuterie);
  }, [maintenant, enEchec]);
  const ecoule = Math.max(0, Math.floor(((maintenant ?? horloge) - depuis.getTime()) / 1000));
  return <>
      {enEchec ? <p role="alert" className="mt-2 text-sm leading-5 text-danger-ink">{echec}</p>
        : etape ? <div role="status" aria-live="polite" className="mt-0.5 flex min-h-7 items-center gap-2 text-xs leading-4 text-ink-500">
          <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-3"><IconeDOutil kind={etape.kind} size={12} /></span>
          <span className="min-w-0 flex-1 truncate">{etape.label}</span>
          {etape.state === 'running' ? <span aria-label="En cours" className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-border border-t-ink-500 motion-reduce:animate-none" />
            : <span className={cn('flex shrink-0 items-center gap-1', etape.state === 'error' && 'text-danger-ink')}>
              {etape.state === 'done' ? <IconCheck size={13} /> : <IconClose size={13} />}
              {etape.state === 'done' ? 'Terminé' : etape.outcome === 'denied' ? 'Refusé' : 'Échec'}
            </span>}
        </div>
          : <div role="status" aria-live="polite" className="mt-0.5 flex min-h-7 items-center gap-2 text-xs leading-4 text-ink-500">
            <span aria-hidden="true" className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-border border-t-ink-500 motion-reduce:animate-none" />{statut || 'Lia réfléchit…'}
          </div>}
      {!enEchec && ecoule >= SECONDES_AVANT_LE_COMPTEUR && <p className="mt-1 text-xs leading-4 text-ink-500">
        {ecoule < SECONDES_AVANT_LE_LONG ? `Toujours en cours (${ecoule} s).`
          : detailLong !== undefined ? detailLong(ecoule) : `C’est plus long que d’habitude (${ecoule} s).`}
      </p>}
  </>;
}
